import React, { useState, useEffect, useCallback } from 'react';
import { BookmarkIcon } from './Icons';
import { bookmarksAPI } from '../services/api';
import VideoPlayerModal from './VideoPlayerModal';
import VideoEmbedModal from './VideoEmbedModal';
import { isThinkificEmbed } from '../utils/videoSource';
import { activateOnKey } from '../utils/a11y';
import styles from '../styles/ContentCard.module.css';
import videoStyles from '../styles/VideoCard.module.css';

/**
 * Video Card Component
 * Displays course video cards with thumbnail and play button
 *
 * Props:
 * - id: Video ID
 * - videoId: Thinkific video ID
 * - title: Video title
 * - courseName: Course name
 * - description: Video description (optional)
 * - videoUrl: Direct video URL
 * - lessonUrl: Lesson page URL
 * - thumbnailUrl: Video thumbnail URL
 * - onClick: Optional click handler (receives video data)
 * - showBookmark: Show bookmark button (default: true)
 * - isBookmarked: Initial bookmark state (optional, will check API if not provided)
 */
function VideoCard({
  id,
  videoId,
  title,
  courseName,
  description,
  videoUrl,
  lessonUrl,
  thumbnailUrl,
  onClick,
  showBookmark = true,
  isBookmarked: initialIsBookmarked,
  isShared = false
}) {
  const [isBookmarked, setIsBookmarked] = useState(initialIsBookmarked || false);
  const [isTogglingBookmark, setIsTogglingBookmark] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showEmbedModal, setShowEmbedModal] = useState(false);

  // Bookmarks key content by numeric DB id. An unhydrated content tag can carry
  // a string slug instead — that can't be bookmarked, so resolve to null.
  const numericId = (id != null && id !== '' && Number.isInteger(Number(id)))
    ? Number(id)
    : null;

  const API_URL = process.env.REACT_APP_API_URL || '';
  const defaultThumbnailIndex = (() => {
    const key = title || '';
    let hash = 0;
    for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) | 0;
    return (Math.abs(hash) % 6) + 1;
  })();
  const defaultThumbnailFile = defaultThumbnailIndex === 1
    ? 'default_thumnail.png'
    : `default_thumnail${defaultThumbnailIndex}.png`;
  const resolvedThumbnailUrl = thumbnailUrl || `${API_URL}/${defaultThumbnailFile}`;

  // Check if video is self-hosted (from Strapi media) vs external link
  const isSelfHosted = videoUrl && (
    videoUrl.includes('/uploads/') ||
    videoUrl.includes('strapi') ||
    (!lessonUrl && !videoUrl.includes('youtube') && !videoUrl.includes('vimeo') && !videoUrl.includes('thinkific'))
  );

  // Thinkific lesson videos are embeddable player pages — play inline in an
  // iframe modal rather than opening the Thinkific lesson in a new tab.
  const isEmbeddable = !isSelfHosted && isThinkificEmbed(videoUrl);

  // Id used to re-mint a fresh signed URL before playback (accepts numeric PK
  // or the slug-like videoId field).
  const playId = (id != null && id !== '') ? id : videoId;

  const checkBookmarkStatus = useCallback(async () => {
    try {
      const result = await bookmarksAPI.isBookmarked('video', numericId);
      setIsBookmarked(result.bookmarked);
    } catch (error) {
      console.error('[VideoCard] Failed to check bookmark status:', error);
    }
  }, [numericId]);

  // Check if bookmarked on mount (only if not provided as prop)
  useEffect(() => {
    if (showBookmark && numericId != null && initialIsBookmarked === undefined) {
      checkBookmarkStatus();
    }
  }, [numericId, showBookmark, initialIsBookmarked, checkBookmarkStatus]);

  // Update local state when prop changes
  useEffect(() => {
    if (initialIsBookmarked !== undefined) {
      setIsBookmarked(initialIsBookmarked);
    }
  }, [initialIsBookmarked]);

  const handleBookmarkClick = async (e) => {
    e.stopPropagation(); // Prevent card click
    if (isTogglingBookmark) return;

    setIsTogglingBookmark(true);
    try {
      const result = await bookmarksAPI.toggleBookmark('video', numericId);
      setIsBookmarked(result.bookmarked);
    } catch (error) {
      console.error('[VideoCard] Failed to toggle bookmark:', error);
    } finally {
      setIsTogglingBookmark(false);
    }
  };

  const handleClick = (e) => {
    // activateOnKey already prevented the default (page scroll on Space), so
    // guard the event access — this also runs for keyboard activation.
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }

    // If custom onClick provided, use it
    if (onClick) {
      onClick({ id, videoId, title, courseName, videoUrl, lessonUrl, thumbnailUrl });
      return;
    }

    // For self-hosted videos, show inline modal player
    if (isSelfHosted && videoUrl) {
      setShowVideoModal(true);
      return;
    }

    // For Thinkific lesson videos, play inline in an iframe modal
    if (isEmbeddable) {
      setShowEmbedModal(true);
      return;
    }

    // For external videos: Navigate to lesson URL if available, otherwise video URL
    const targetUrl = lessonUrl || videoUrl;
    if (targetUrl) {
      window.open(targetUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div
      className={styles.contentCard}
      role="button"
      tabIndex={0}
      aria-label={`Play video: ${title}`}
      onClick={handleClick}
      onKeyDown={activateOnKey(handleClick)}
      style={{ cursor: 'pointer' }}
    >
      <div className={styles.contentCardImage}>
        <img src={resolvedThumbnailUrl} alt={title} className={styles.image} />

        {/* Play button overlay */}
        <div className={videoStyles.playButtonOverlay}>
          <div className={videoStyles.playButton}>
            <svg
              width="48"
              height="48"
              viewBox="0 0 48 48"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="24" cy="24" r="24" fill="rgba(255, 255, 255, 0.95)" />
              <path
                d="M19 15L34 24L19 33V15Z"
                fill="#1b4942"
              />
            </svg>
          </div>
        </div>

        {/* Course badge */}
        <div className={styles.contentCardBadge}>
          {courseName || 'Video'}
        </div>

        {/* Shared-with-you chip */}
        {isShared && <div className={styles.sharedChip}>Shared with you before</div>}

        {/* Bookmark button */}
        {showBookmark && numericId != null && (
          <button
            className={styles.bookmarkButton}
            onClick={handleBookmarkClick}
            disabled={isTogglingBookmark}
            aria-label={isBookmarked ? `Remove bookmark from ${title}` : `Bookmark ${title}`}
          >
            <BookmarkIcon size={20} filled={isBookmarked} />
          </button>
        )}
      </div>

      <div className={styles.contentCardContent}>
        <div className={styles.contentCardTitle}>{title}</div>

        {description && (
          <div className={styles.contentCardDescription}>
            {description}
          </div>
        )}

        <div className={styles.contentCardMeta}>
          <div className={styles.contentCardMetaItem}>
            ▶ {(isSelfHosted || isEmbeddable) ? 'Play Video' : 'Watch Video'}
          </div>
        </div>
      </div>

      {/* Video Player Modal for self-hosted videos */}
      {showVideoModal && (
        <VideoPlayerModal
          videoUrl={videoUrl}
          title={title}
          onClose={() => setShowVideoModal(false)}
        />
      )}

      {/* Iframe Modal for Thinkific lesson videos */}
      {showEmbedModal && (
        <VideoEmbedModal
          playId={playId}
          videoUrl={videoUrl}
          title={title}
          onClose={() => setShowEmbedModal(false)}
        />
      )}
    </div>
  );
}

export default VideoCard;
